<script setup lang="ts">
	import { computed, ref, watch } from 'vue'

	import { useCurrencyFormat } from '@/composables/useCurrencyFormat'
	import useDateFormat from '@/composables/useDateFormat'
	import usePlans from '@/modules/plans/composables/usePlans'
	import useCompanySubscriptions from '@/modules/plans/composables/useCompanySubscriptions'
	import type { Plan, PlanFeature } from '@/modules/plans/interfaces/plan'
	import {
		Dialog,
		DialogDescription,
		DialogHeader,
		DialogScrollContent,
		DialogTitle,
	} from '@/components/ui/dialog'

	interface Props {
		open: boolean
		companyId?: number
		subscriptionId?: number
		currentPlanCode?: string
		/** When false the plan-selection CTAs are hidden (read-only comparison). */
		canManage?: boolean
	}

	const props = withDefaults(defineProps<Props>(), { canManage: true })
	const emit = defineEmits<{
		'update:open': [value: boolean]
		select: [planCode: string]
	}>()

	const { data: plansData, isLoading: plansLoading } = usePlans()

	const companyIdRef = computed(() => props.companyId)
	const { useSwapPreview } = useCompanySubscriptions({ companyId: companyIdRef })

	// Plans sorted as returned by the catalog; featureMatrix keys by plan code.
	const plans = computed<Plan[]>(() => plansData.value?.plans ?? [])
	const featureMatrix = computed(() => plansData.value?.featureMatrix ?? [])

	const selectedPlanCode = ref<string | undefined>(undefined)

	// Reset selection whenever the modal is reopened.
	watch(
		() => props.open,
		(open) => {
			if (!open) selectedPlanCode.value = undefined
		}
	)

	const subscriptionIdRef = computed(() => props.subscriptionId)
	const { data: preview, isFetching: previewLoading } = useSwapPreview(
		subscriptionIdRef,
		selectedPlanCode
	)

	const isCurrent = (code: string) => code === props.currentPlanCode

	/**
	 * La empresa trae un plan que no está en el catálogo público.
	 *
	 * Esta modal se alimenta de /v1/plans y de su featureMatrix, que sólo traen
	 * planes públicos: un plan asignado no tiene columna en la matriz, y pintar
	 * su tarjeta con "—" en cada renglón diría que no incluye nada, justo al
	 * revés de lo que suele ser. En vez de eso se avisa que su plan queda fuera
	 * de la comparativa; el detalle real de su plan lo tiene el panel de
	 * suscripción, que sí lo recibe.
	 */
	const currentPlanIsAssigned = computed(
		() =>
			!!props.currentPlanCode &&
			!plansLoading.value &&
			plans.value.length > 0 &&
			!plans.value.some((p) => p.code === props.currentPlanCode)
	)

	function priceLabel(plan: Plan) {
		if (!plan.priceMxn || plan.priceMxn === 0) return 'Gratis'
		const interval = plan.billingInterval === 'annual' ? 'año' : 'mes'
		return `${useCurrencyFormat(plan.priceMxn, 'MXN')} / ${interval}`
	}

	function fmtDate(iso: string | null | undefined) {
		return iso ? useDateFormat(iso).dateNoDay : '—'
	}

	function cellValue(row: Record<string, string>, code: string) {
		return row[code] ?? '—'
	}

	function onSelect(code: string) {
		if (isCurrent(code)) return
		selectedPlanCode.value = code
	}

	function confirm() {
		if (!selectedPlanCode.value) return
		emit('select', selectedPlanCode.value)
		emit('update:open', false)
	}

	// ── Downgrade over-limit detection ──────────────────────────────────────────
	type ParsedFeature = { limit: number | null; included: boolean }

	function parseFeature(f: PlanFeature): ParsedFeature {
		try {
			const v = JSON.parse(f.value) as {
				limit?: number | null
				included?: boolean
			}
			return {
				limit: v.limit === undefined ? null : v.limit,
				included: v.included === true,
			}
		} catch {
			return { limit: null, included: false }
		}
	}

	// `null` limit means unlimited → treated as the largest possible value.
	const asNumber = (limit: number | null) =>
		limit === null ? Number.POSITIVE_INFINITY : limit

	const selectedPlan = computed(() =>
		plans.value.find((p) => p.code === selectedPlanCode.value)
	)
	const currentPlan = computed(() =>
		plans.value.find((p) => p.code === props.currentPlanCode)
	)

	// Features the company would lose or see reduced when downgrading.
	const lostFeatures = computed<string[]>(() => {
		if (preview.value?.swapType !== 'downgrade') return []
		const current = currentPlan.value
		const target = selectedPlan.value
		if (!current || !target) return []

		const targetByCode = new Map(target.features.map((f) => [f.code, f]))
		const lost: string[] = []

		for (const cur of current.features) {
			const tgt = targetByCode.get(cur.code)
			const curParsed = parseFeature(cur)

			// Feature absent from the target plan: it simply isn't offered there.
			// Distinguish this from "reduced to 0" so we don't imply a quota cut.
			if (!tgt) {
				const currentlyGranted =
					cur.kind === 'boolean' ? curParsed.included : curParsed.limit !== 0
				if (currentlyGranted) {
					lost.push(`${cur.name}: no incluida en ${target.name}`)
				}
				continue
			}

			const tgtParsed = parseFeature(tgt)

			if (cur.kind === 'boolean') {
				if (curParsed.included && !tgtParsed.included) {
					lost.push(`Perderás: ${cur.name}`)
				}
				continue
			}

			// limit kind, present in both plans
			const curLimit = asNumber(curParsed.limit)
			const tgtLimit = asNumber(tgtParsed.limit)
			if (tgtLimit < curLimit) {
				const tgtText =
					tgtParsed.limit === null ?
						'ilimitado'
					:	`${tgtParsed.limit}${cur.unit ? ` ${cur.unit}` : ''}`
				lost.push(`${cur.name}: bajará a ${tgtText}`)
			}
		}

		return lost
	})

	const showPreview = computed(
		() => !!selectedPlanCode.value && !!props.subscriptionId
	)
</script>

<template>
	<Dialog
		:open="open"
		@update:open="(v: boolean) => emit('update:open', v)"
	>
		<DialogScrollContent class="sm:max-w-3xl">
			<DialogHeader>
				<DialogTitle>Comparar planes</DialogTitle>
				<DialogDescription>
					Revisa qué incluye cada plan y elige el que mejor se adapte a tu
					empresa.
				</DialogDescription>
				<p
					v-if="!canManage"
					class="mt-1 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700"
				>
					Solo un administrador con permiso de facturación puede cambiar de
					plan. Esta vista es informativa.
				</p>
				<p
					v-if="currentPlanIsAssigned"
					class="mt-1 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800"
				>
					Tu plan actual es un plan asignado por Enlace y no forma parte de
					esta comparativa. Si cambias a uno de los planes de abajo, no vas a
					poder regresar por tu cuenta.
				</p>
			</DialogHeader>

			<!-- Skeleton -->
			<div
				v-if="plansLoading"
				class="space-y-3 py-4"
			>
				<div class="h-10 w-full animate-pulse rounded bg-gray-100" />
				<div class="h-40 w-full animate-pulse rounded bg-gray-100" />
			</div>

			<template v-else>
				<!-- ── Comparison table ── -->
				<div class="overflow-x-auto">
					<table class="w-full border-collapse text-sm">
						<thead>
							<tr>
								<th
									class="sticky left-0 z-10 bg-white px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400"
								>
									Característica
								</th>
								<th
									v-for="plan in plans"
									:key="plan.code"
									:class="[
										'px-3 py-3 text-center align-top',
										isCurrent(plan.code) ? 'bg-blue-50/60' : '',
									]"
								>
									<div class="flex flex-col items-center gap-1">
										<span class="text-sm font-bold text-gray-900">{{
											plan.name
										}}</span>
										<span class="text-xs font-medium text-gray-500">{{
											priceLabel(plan)
										}}</span>
										<span
											v-if="isCurrent(plan.code)"
											class="mt-1 rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
										>
											Plan actual
										</span>
									</div>
								</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="row in featureMatrix"
								:key="row.feature"
								class="border-t border-gray-100"
							>
								<td
									class="sticky left-0 z-10 bg-white px-3 py-2.5 text-left font-medium text-gray-700"
								>
									{{ row.feature }}
								</td>
								<td
									v-for="plan in plans"
									:key="plan.code"
									:class="[
										'px-3 py-2.5 text-center text-gray-600',
										isCurrent(plan.code) ? 'bg-blue-50/40' : '',
									]"
								>
									{{ cellValue(row, plan.code) }}
								</td>
							</tr>
							<!-- CTA row -->
							<tr class="border-t border-gray-200">
								<td class="sticky left-0 z-10 bg-white px-3 py-3" />
								<td
									v-for="plan in plans"
									:key="plan.code"
									:class="[
										'px-3 py-3 text-center',
										isCurrent(plan.code) ? 'bg-blue-50/40' : '',
									]"
								>
									<span
										v-if="isCurrent(plan.code)"
										class="text-xs font-semibold text-blue-700"
									>
										Tu plan
									</span>
									<button
										v-else-if="canManage"
										type="button"
										:class="[
											'w-full rounded-lg px-3 py-2 text-xs font-semibold transition-colors',
											selectedPlanCode === plan.code ?
												'bg-[#253C70] text-white'
											:	'border border-gray-300 bg-white text-gray-900 hover:bg-gray-50',
										]"
										@click="onSelect(plan.code)"
									>
										{{
											selectedPlanCode === plan.code ?
												'Seleccionado'
											:	'Elegir'
										}}
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- ── Change preview ── -->
				<div
					v-if="selectedPlanCode && !isCurrent(selectedPlanCode)"
					class="mt-4 rounded-xl border border-gray-200 bg-gray-50/60 p-4"
				>
					<!-- Loading -->
					<div
						v-if="showPreview && previewLoading"
						class="flex items-center gap-2 text-sm text-gray-500"
					>
						<span
							class="material-symbols-outlined animate-spin text-[18px]"
							>progress_activity</span
						>
						Calculando el cambio…
					</div>

					<!-- Upgrade preview -->
					<template v-else-if="showPreview && preview?.swapType === 'upgrade'">
						<p class="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
							<span class="material-symbols-outlined text-[18px] text-emerald-600"
								>trending_up</span
							>
							Mejorarás a {{ selectedPlan?.name }}
						</p>
						<p class="mt-1 text-sm text-gray-600">
							Pagarás
							<span class="font-semibold text-gray-900">{{
								useCurrencyFormat(preview?.proratedAmount ?? 0, 'MXN')
							}}</span>
							hoy (prorrateado por {{ preview?.remainingDays ?? 0 }} días
							restantes). Tu próximo ciclo será
							<span class="font-semibold text-gray-900">{{
								selectedPlan ? priceLabel(selectedPlan) : ''
							}}</span
							>.
						</p>
					</template>

					<!-- Downgrade preview -->
					<template
						v-else-if="showPreview && preview?.swapType === 'downgrade'"
					>
						<p class="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
							<span class="material-symbols-outlined text-[18px] text-amber-600"
								>trending_down</span
							>
							Cambiarás a {{ selectedPlan?.name }}
						</p>
						<p class="mt-1 text-sm text-gray-600">
							Mantienes el acceso a tu plan actual hasta el
							<span class="font-semibold text-gray-900">{{
								fmtDate(preview?.effectiveAt)
							}}</span
							>. Después cambiarás a {{ selectedPlan?.name }}.
						</p>
						<ul
							v-if="lostFeatures.length"
							class="mt-3 space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-3"
						>
							<li
								v-for="(warning, idx) in lostFeatures"
								:key="idx"
								class="flex items-start gap-1.5 text-xs text-amber-800"
							>
								<span class="material-symbols-outlined text-[14px]"
									>warning</span
								>
								<span>{{ warning }}</span>
							</li>
						</ul>
					</template>

					<!-- No active subscription (new signup): no proration to preview -->
					<template v-else-if="!props.subscriptionId">
						<p class="text-sm text-gray-600">
							Te suscribirás al plan
							<span class="font-semibold text-gray-900">{{
								selectedPlan?.name
							}}</span>
							({{ selectedPlan ? priceLabel(selectedPlan) : '' }}).
						</p>
					</template>

					<button
						type="button"
						class="mt-4 w-full rounded-xl bg-[#253C70] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e3260] disabled:opacity-50"
						:disabled="showPreview && previewLoading"
						@click="confirm"
					>
						Continuar con {{ selectedPlan?.name }}
					</button>
				</div>
			</template>
		</DialogScrollContent>
	</Dialog>
</template>
